import type { ReactNode } from 'react';
import { z } from 'zod';

export type SelfStep =
  | 'landing'
  | 'objetivo'
  | 'perfil'
  | 'parametros'
  | 'revisao'
  | 'pagamento'
  | 'errorPayment'
  | 'terms'
  | 'waitingPayment'
  | 'success';

export type ObjetivoSelf = 'desocupacao' | 'valores' | 'clausula';

/** Plano do catálogo da Lexi servido pelo core (`GET /negociacao-self/planos`). */
export const planoNegociacaoSelfSchema = z.object({
  variationId: z.string().min(1),
  tier: z.enum(['start', 'pro', 'scale']),
  nome: z.string(),
  precoMensal: z.string(),
  franquiaEnvios: z.number().int().nonnegative(),
  porEnvio: z.string(),
  taxaExito: z.string().nullable(),
  destaque: z.boolean(),
  badge: z.string().nullable(),
});

export type PlanoNegociacaoSelf = z.infer<typeof planoNegociacaoSelfSchema>;

export interface PlanoNegociacaoCard {
  variationId: string;
  nome: string;
  preco: string;
  recursos: string[];
  recomendado: boolean;
}

export type AutorMensagem = 'lexi' | 'user';

export interface ChatMensagemSelf {
  id: string;
  autor: AutorMensagem;
  texto: string;
}

export interface PerfilSelf {
  nome: string;
  email: string;
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  site: string;
  sobre: string;
}

export interface ConsultaCnpjNegociacaoSelfResultado {
  encontrado: boolean;
  razaoSocial: string;
  nomeFantasia: string;
}

export interface ObjetivoOpcao {
  valor: ObjetivoSelf;
  titulo: string;
  descricao: string;
  disponivel: boolean;
}

export interface BuscaContextoResultado {
  leadId: string;
  accessToken: string;
  encontrado: boolean;
  sobre: string;
}

export interface SessaoSelf {
  leadId: string;
  accessToken: string;
  objetivo: ObjetivoSelf;
  invoiceUrl: string | null;
  variationId: string | null;
  termosAceitos: boolean;
}

export type TipoCampoParametro =
  | 'STRING'
  | 'NUMBER'
  | 'FINANCEIRO'
  | 'BOOLEAN'
  | 'DATE'
  | 'JSON'
  | 'ENUM';

export interface CampoParametroSelf {
  key: string;
  label: string;
  helper: string;
  tipo: TipoCampoParametro;
  obrigatorio: boolean;
  placeholder: string | null;
  prefixo: string | null;
  sufixo: string | null;
}

export type ValoresParametros = Record<string, string | boolean>;

export interface CriarAgenteResultado {
  mensagemInicial: string;
}

export type PagamentoSelfStatus = 'PENDENTE' | 'PAGO' | 'CANCELADO' | 'ERRO';

export interface IniciarPagamentoResultado {
  invoiceUrl: string | null;
  status: PagamentoSelfStatus;
}

export interface ConsultaPagamentoResultado {
  status: PagamentoSelfStatus;
  convertido: boolean;
}

export interface CriarAgentePayload {
  leadId: string;
  accessToken: string;
  objetivo: ObjetivoSelf;
  sobre: string;
  valores: ValoresParametros;
}

export interface IniciarPagamentoPayload {
  leadId: string;
  accessToken: string;
  variationId: string;
  aceiteTermos: boolean;
  termosVersao: string;
}

export interface SimulacaoPayload {
  leadId: string;
  accessToken: string;
  mensagem: string;
  historico: Array<{ autor: AutorMensagem; texto: string }>;
}

export interface ResumoLinhaProps {
  rotulo: string;
  valor: ReactNode;
}

export interface WizardStepView {
  label: string;
  filled: boolean;
  active: boolean;
}

export interface TermoSecao {
  titulo: string;
  corpo: string;
}

export interface AbortableThunk {
  abort: (reason?: string) => void;
}
